/**
 * Media File Utility Functions
 * Handles sorting and flattening logic for media files with modified file relationships
 */

import { MediaFile } from "@survey/types/survey.media.types";

/**
 * Flattens modified files with their originals for flyer/flier media types.
 * Modified files replace their original's position in the list.
 * 
 * Algorithm:
 * 1. Iterate through each file in the array
 * 2. Check if any file has modifiedFromMediaId matching current file's mediaId
 * 3. If found, the current file is an original with a modified version
 * 4. Replace original with modified at original's index position
 * 5. Remove modified from its separate position
 * 6. Result: Modified files inherit their original's sort order
 * 
 * Example:
 * Before:
 * [
 *   { mediaId: 'A', order: '1', modifiedFromMediaId: null },
 *   { mediaId: 'B', order: '2', modifiedFromMediaId: null },
 *   { mediaId: 'A-modified', order: '3', modifiedFromMediaId: 'A' }
 * ]
 * 
 * After:
 * [
 *   { mediaId: 'A-modified', order: '3', modifiedFromMediaId: 'A' },  // Replaced 'A'
 *   { mediaId: 'B', order: '2', modifiedFromMediaId: null }
 * ]
 * 
 * @param files - Array of media files
 * @returns Flattened array with modified files replacing originals
 */
export const flattenMediaFilesWithModifiedFiles = (
  files: MediaFile[]
): MediaFile[] => {
  const result = [...files];

  // For each file, check if it's an original with a modified version
  result.forEach((item, i) => {
    // Find modified version of this file
    const modifiedIndex = result.findIndex(
      (res) => item.mediaId === res.modifiedFromMediaId
    );

    if (modifiedIndex > -1) {
      // Replace original with modified version at original's position
      result[i] = result[modifiedIndex];
      // Remove modified version from its separate position
      result.splice(modifiedIndex, 1);
    }
  });

  return result;
};

/**
 * Sorts files by order property for standard file types (images, floor plans)
 * 
 * @param files - Array of media files
 * @returns Sorted array by order property
 */
export const sortFilesByOrder = (files: MediaFile[]): MediaFile[] => {
  return [...files].sort((a, b) => {
    const orderA = parseInt(a.order || "0");
    const orderB = parseInt(b.order || "0");
    return orderA - orderB;
  });
};
